import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { appConfig } from '@/config/environment'
import { useAuth } from '@/features/auth/AuthProvider'
import { supabase } from '@/lib/supabase/client'
import { type AppPermission, toPermissions } from './permissions'

export type UserProfile = Readonly<{ id: string; displayName: string | null; onboardingCompletedAt: string | null; permissions: readonly AppPermission[] | null }>
export type ProfileContextValue = Readonly<{ profile: UserProfile | null; loading: boolean; error: string | null; updateDisplayName: (name: string) => Promise<void>; refresh: () => Promise<void> }>
export const ProfileContext = createContext<ProfileContextValue | null>(null)
// A completed profile is cached so a load paints straight away, offline too,
// but every load also checks it against the server behind that paint: the
// profile carries the person's permissions, which someone with Team can change
// at any time, so a refresh is enough to pick up a change.
type CachedProfile = UserProfile & Readonly<{ cachedAt?: number }>
const cacheKey = (userId: string) => `${appConfig.storageNamespace}:profile:${userId}`
// `permissions` is a computed column (public.permissions(profiles)): what this
// person can use, in the same request as their name.
const PROFILE_COLUMNS = 'id, display_name, onboarding_completed_at, permissions'
const toProfile = (row: { id: string; display_name: string | null; onboarding_completed_at: string | null; permissions?: unknown }): UserProfile => ({ id: row.id, displayName: row.display_name, onboardingCompletedAt: row.onboarding_completed_at, permissions: toPermissions(row.permissions) })

function readCache(id: string) { try { const value = localStorage.getItem(cacheKey(id)); return value ? JSON.parse(value) as CachedProfile : null } catch { return null } }
function writeCache(profile: UserProfile) { try { localStorage.setItem(cacheKey(profile.id), JSON.stringify({ ...profile, cachedAt: Date.now() })) } catch { /* cache is an offline enhancement */ } }

export function ProfileProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const userId = session?.user.id
  const load = useCallback(async () => {
    // Without a user there is nothing to resolve, so the profile is unsettled
    // rather than absent. Reporting `loading: false` here would put the provider
    // into the "settled and empty" state during the gap between mount and session
    // restoration — which the onboarding redirect reads as "this user has no
    // profile" and acts on the moment auth resolves.
    if (!userId) { setProfile(null); setError(null); setLoading(true); return }
    const cached = readCache(userId)
    // A profile cached before permissions existed doesn't know them: it
    // paints with `permissions: null` (unknown, so nothing is hidden) and is
    // always refreshed. Treating it as "no permissions" would leave someone
    // who is offline with no tabs at all.
    const permissionsCached = Array.isArray(cached?.permissions)
    const usable = cached?.onboardingCompletedAt && cached.displayName ? { ...cached, permissions: permissionsCached ? toPermissions(cached.permissions) : null } : null
    if (usable) { setProfile(usable); setLoading(false) }
    else { setLoading(true) }
    const { data, error: requestError } = await supabase.from('profiles').select(PROFILE_COLUMNS).eq('id', userId).maybeSingle()
    if (requestError || !data) {
      if (!cached?.onboardingCompletedAt) { setError('Your profile could not be loaded. Check your connection and try again.'); setProfile(null) }
    } else { const next = toProfile(data); setProfile(next); setError(null); if (next.onboardingCompletedAt && next.displayName) writeCache(next) }
    setLoading(false)
  }, [userId])
  // Deferred rather than called straight from the effect body: `load` settles
  // state synchronously before its first await, which the effect rule forbids.
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0)
    return () => window.clearTimeout(timer)
  }, [load])

  // The cache is keyed by user id, so it can never be shown to the next person
  // to use the device, but it is still the signed-out user's data left behind
  // on it. Drop it with the session rather than letting it outlive them.
  const lastUserId = useRef(userId)
  useEffect(() => {
    if (userId) { lastUserId.current = userId; return }
    const previous = lastUserId.current
    lastUserId.current = undefined
    if (!previous) return
    try { localStorage.removeItem(cacheKey(previous)) } catch { /* storage may be unavailable */ }
  }, [userId])
  const updateDisplayName = useCallback(async (rawName: string) => {
    const displayName = rawName.trim()
    if (!displayName) throw new Error('Enter your name to continue.')
    if (!userId) throw new Error('Please sign in again.')
    if (!navigator.onLine) throw new Error('Connect to the internet to save your name.')
    const { data, error: requestError } = await supabase.from('profiles').update({ display_name: displayName }).eq('id', userId).select(PROFILE_COLUMNS).single()
    if (requestError || !data) throw new Error('Your name could not be saved. Please try again.')
    const next = toProfile(data); setProfile(next); setError(null); writeCache(next)
  }, [userId])
  const refresh = useCallback(() => load(), [load])
  const value = useMemo(() => ({ profile, loading, error, updateDisplayName, refresh }), [profile, loading, error, updateDisplayName, refresh])
  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>
}
export function useProfile() { const value = useContext(ProfileContext); if (!value) throw new Error('useProfile must be used within ProfileProvider.'); return value }
